/**
 * Panel unter dem Buchungskalender: zeigt den gewählten Slot mit editierbaren
 * Datum-/Start-/Ende-Feldern.
 *
 * Hintergrund: Auf Touch-Geräten (iPhone/Android) funktioniert Drag-Select in
 * react-big-calendar nicht zuverlässig — ein Tap liefert nur eine Zelle, die
 * dann auf die Mindestdauer (60 Min.) aufgerundet wird. Über die Felder hier
 * lässt sich der Slot nachträglich verlängern oder verschieben.
 *
 * Validierung spiegelt backend/app/services/bookings.py::validate_slot_shape:
 * Ende nach Start, Mindestdauer max(60, room.min_duration_minutes),
 * 30-Minuten-Raster. Konflikte werden gegen den Tages-Feed geprüft; die
 * autoritative Prüfung bleibt im Backend (409 + DB-Constraint).
 */

import { useMemo } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";

import { Button } from "@/components/Button";
import { useCalendarFeed } from "@/hooks/useCalendar";
import { formatRange } from "@/lib/format";
import type { Room } from "@/types/api";

export interface SlotRange {
  start: Date;
  end: Date;
}

interface SlotRequestPanelProps {
  room: Room;
  value: SlotRange | null;
  onChange: (slot: SlotRange) => void;
}

function withTime(base: Date, hhmm: string): Date {
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date(base);
  d.setHours(h, m, 0, 0);
  return d;
}

function withDate(base: Date, yyyyMmDd: string): Date {
  const [y, mo, day] = yyyyMmDd.split("-").map(Number);
  const d = new Date(base);
  d.setFullYear(y, mo - 1, day);
  return d;
}

function durationLabel(minutes: number): string {
  const hours = minutes / 60;
  const formatted = new Intl.NumberFormat("de-DE", {
    maximumFractionDigits: 1,
  }).format(hours);
  return `${formatted} ${hours === 1 ? "Stunde" : "Stunden"}`;
}

const timeFieldCls =
  "mt-1 block w-full rounded border border-line bg-surface px-3 py-2 text-body-sm text-ink shadow-sm focus:border-primary focus:ring-2 focus:ring-primary/30 focus:outline-none";
const timeLabelCls = "block text-caption font-medium uppercase tracking-wider text-ink-muted";

export function SlotRequestPanel({ room, value, onChange }: SlotRequestPanelProps) {
  const dayRange = useMemo(() => {
    if (!value) return null;
    const s = new Date(value.start);
    s.setHours(0, 0, 0, 0);
    const e = new Date(s);
    e.setDate(e.getDate() + 1);
    return { starts_at: s.toISOString(), ends_at: e.toISOString() };
  }, [value]);

  const { data: dayEvents } = useCalendarFeed(
    {
      starts_at: dayRange?.starts_at ?? "",
      ends_at: dayRange?.ends_at ?? "",
      room_id: room.id,
    },
    Boolean(dayRange),
  );

  const minutes = value ? (value.end.getTime() - value.start.getTime()) / 60000 : 0;
  const minDuration = Math.max(60, room.min_duration_minutes);

  const error = useMemo(() => {
    if (!value) return null;
    if (minutes <= 0) return "Das Ende muss nach dem Start liegen.";
    if (minutes < minDuration) return `Mindestdauer: ${minDuration} Minuten.`;
    if (minutes % 30 !== 0) return "Bitte Zeiten im 30-Minuten-Raster wählen (z. B. 10:00 oder 10:30).";
    if (value.start.getTime() < Date.now()) return "Der Start liegt in der Vergangenheit.";
    const conflict = (dayEvents ?? []).some(
      (e) => new Date(e.starts_at) < value.end && new Date(e.ends_at) > value.start,
    );
    if (conflict) return "Der Zeitraum überschneidet sich mit einer belegten oder gesperrten Zeit.";
    return null;
  }, [value, minutes, minDuration, dayEvents]);

  if (!value) {
    return (
      <section className="flex flex-col items-start gap-3 rounded-card bg-surface-warm p-6 md:flex-row md:items-center md:justify-between">
        <p className="text-body-sm text-ink-soft">
          Wähle einen Slot im Kalender oder gehe direkt zum Buchungsformular.
        </p>
        <Link to={`/buchen/${room.slug}`}>
          <Button size="lg">Slot anfragen</Button>
        </Link>
      </section>
    );
  }

  const bookingHref = `/buchen/${room.slug}?start=${encodeURIComponent(
    value.start.toISOString(),
  )}&end=${encodeURIComponent(value.end.toISOString())}`;

  return (
    <section className="rounded-card bg-surface-warm p-6">
      <p className="text-caption uppercase tracking-wider text-ink-muted">Gewählter Slot</p>
      <p className="mt-1 font-display text-h4 text-ink">
        {formatRange(value.start.toISOString(), value.end.toISOString())}
      </p>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label>
          <span className={timeLabelCls}>Datum</span>
          <input
            type="date"
            value={format(value.start, "yyyy-MM-dd")}
            onChange={(e) => {
              if (!e.target.value) return;
              onChange({
                start: withDate(value.start, e.target.value),
                end: withDate(value.end, e.target.value),
              });
            }}
            className={timeFieldCls}
          />
        </label>
        <label>
          <span className={timeLabelCls}>Start</span>
          <input
            type="time"
            step={1800}
            value={format(value.start, "HH:mm")}
            onChange={(e) => {
              if (!e.target.value) return;
              // Start verschieben = ganzen Block verschieben (Dauer bleibt).
              const nextStart = withTime(value.start, e.target.value);
              const duration = value.end.getTime() - value.start.getTime();
              onChange({ start: nextStart, end: new Date(nextStart.getTime() + duration) });
            }}
            className={timeFieldCls}
          />
        </label>
        <label>
          <span className={timeLabelCls}>Ende</span>
          <input
            type="time"
            step={1800}
            value={format(value.end, "HH:mm")}
            onChange={(e) => {
              if (!e.target.value) return;
              onChange({ start: value.start, end: withTime(value.end, e.target.value) });
            }}
            className={timeFieldCls}
          />
        </label>
      </div>

      <div className="mt-4 flex flex-col items-start gap-3 md:flex-row md:items-center md:justify-between">
        {error ? (
          <p className="text-body-sm font-medium text-danger">{error}</p>
        ) : (
          <p className="text-body-sm text-ink-soft">Dauer: {durationLabel(minutes)}</p>
        )}
        {error ? (
          <Button size="lg" disabled>
            Slot anfragen
          </Button>
        ) : (
          <Link to={bookingHref}>
            <Button size="lg">Slot anfragen</Button>
          </Link>
        )}
      </div>
    </section>
  );
}
